import { approxEqual, Virtualizer } from '@tanstack/react-virtual'

export function getElementOffsetTopFromDocument(node: HTMLElement | null) {
  let top = 0
  while (node) {
    top += node.offsetTop
    node = node.offsetParent as HTMLElement | null
  }
  return top
}

function waitForImgOrDetach(
  container: HTMLElement,
  img: HTMLImageElement,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false
    let mo: MutationObserver | null = null

    const isGone = (): boolean => !img.isConnected || !container.contains(img)

    const cleanup = (): void => {
      img.removeEventListener('load', onLoadOrError)
      img.removeEventListener('error', onLoadOrError)
      mo?.disconnect()
      mo = null
    }

    const finish = (): void => {
      if (done) return
      done = true
      cleanup()
      resolve()
    }

    const onLoadOrError = (): void => {
      if (isGone()) {
        finish()
        return
      }

      void (async () => {
        try {
          await img.decode()
        } catch {
          // ignore decode failures
        }
        finish()
      })()
    }

    // If already removed, ignore immediately
    if (isGone()) {
      finish()
      return
    }

    // If already loaded, decode+finish
    if (img.complete) {
      onLoadOrError()
      return
    }

    img.addEventListener('load', onLoadOrError, { once: true })
    img.addEventListener('error', onLoadOrError, { once: true })

    // Detect removal from container
    mo = new MutationObserver(() => {
      if (isGone()) finish()
    })
    mo.observe(container, { childList: true, subtree: true })
  })
}

export async function waitForImages(container: HTMLElement) {
  const images = Array.from(container.querySelectorAll('img'))

  if (!images.length) return

  await Promise.all(images.map((img) => waitForImgOrDetach(container, img)))
}

type ScrollAlignment = 'auto' | 'start' | 'center' | 'end'
type ScrollBehavior = 'auto' | 'smooth'

type ScrollToIndexOptions = {
  align?: ScrollAlignment
  behavior?: ScrollBehavior
}

export function scrollToIndex(
  virtualizer: Virtualizer<Window | Element, Element>,
  index: number,
  opts: ScrollToIndexOptions & {
    maxAttempts?: number
  } = {},
): Promise<void> {
  const { align: initialAlign = 'auto', behavior, maxAttempts = 10 } = opts

  index = Math.max(0, Math.min(index, virtualizer.options.count - 1))

  return new Promise<void>((resolve, reject) => {
    let attempts = 0
    let rafId: number | null = null
    let done = false

    const cancel = () => {
      if (rafId != null && virtualizer.targetWindow?.cancelAnimationFrame) {
        virtualizer.targetWindow.cancelAnimationFrame(rafId)
      }
      rafId = null
    }

    const fail = (err: Error) => {
      if (done) return
      done = true
      cancel()
      reject(err)
    }

    const succeed = () => {
      if (done) return
      done = true
      cancel()
      resolve()
    }

    const schedule = (fn: () => void) => {
      const w: Window | undefined = virtualizer.targetWindow ?? window
      rafId = w.requestAnimationFrame(fn)
    }

    const tryScroll = (currentAlign: ScrollAlignment) => {
      if (done) return
      if (!virtualizer.targetWindow) return

      const offsetInfo = virtualizer.getOffsetForIndex(index, currentAlign)
      if (!offsetInfo) {
        fail(new Error(`scrollToIndex: failed getOffsetForIndex(${index})`))
        return
      }

      const [offset, align] = offsetInfo

      virtualizer.options.scrollToFn(
        offset,
        { behavior, adjustments: undefined },
        virtualizer,
      )

      schedule(() => {
        if (done) return

        const currentOffset = virtualizer.scrollOffset || 0
        const afterInfo = virtualizer.getOffsetForIndex(index, align)

        if (!afterInfo) {
          fail(new Error(`scrollToIndex: failed getOffsetForIndex(${index})`))
          return
        }

        const [afterOffset] = afterInfo

        if (approxEqual(afterOffset, currentOffset)) {
          succeed()
          return
        }

        attempts += 1
        if (attempts < maxAttempts) {
          schedule(() => tryScroll(align))
        } else {
          fail(
            new Error(
              `scrollToIndex: failed to scroll to index ${index} after ${maxAttempts} attempts`,
            ),
          )
        }
      })
    }

    tryScroll(initialAlign)
  })
}
